import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardTitle, Insight, MetricCard, Grid } from '../components/UI';
import { supabase } from '../supabaseClient';
import { getUserRole } from '../roles';
import { PASSAGE_PRICES, PLAY_CONSOLE_URL, planKey, priceOf } from '../constants/pricing';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';


function fmt(n) { return n == null ? '…' : Number(n).toLocaleString(); }
function fmtDate(s) { return s ? s.slice(0, 10) : '—'; }
function daysSince(ts) {
  if (!ts) return null;
  return Math.round((Date.now() - new Date(ts)) / 86400000);
}

function Skel({ h = 36 }) {
  return (
    <div style={{ height: h, borderRadius: 8, marginBottom: 6,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function Chip({ tone, children }) {
  const c = tone === 'red' ? '#dc3545' : tone === 'amber' ? '#fd7e14' : tone === 'green' ? '#28a745' : '#6F8FA7';
  return (
    <span style={{ display: 'inline-block', fontSize: '.6rem', fontWeight: 600, padding: '1px 7px',
      borderRadius: 8, background: c + '18', color: c, marginRight: 4, marginTop: 3, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

// Policy verdict from the admin_refund_eligibility rollup. `hard` reasons are
// enforced server-side by admin_record_play_refund; `soft` ones are advisory.
function eligVerdict(e, passType, purchasedAt) {
  const hard = []; const soft = [];
  if (passType && planKey(passType) !== 'ANNUAL') hard.push(passType + ' is all-sales-final');
  if (purchasedAt != null) {
    const d2 = daysSince(purchasedAt);
    if (d2 != null && d2 > 14) hard.push('outside 14-day window');
  }
  if (e) {
    if (e.flagged) hard.push('account flagged' + (e.flag_reason ? ' — ' + e.flag_reason : ''));
    if (e.suspended) hard.push('account suspended');
    if ((e.lifetime_refund_count || 0) >= 1) hard.push('lifetime refund quota used');
    if (e.shared_device) soft.push('shared device');
    if (e.last_chargeback_at) soft.push('chargeback history');
    if ((e.same_day_cancel_count || 0) > 0) soft.push(e.same_day_cancel_count + ' same-day cancel' + (e.same_day_cancel_count === 1 ? '' : 's'));
    if ((e.device_count || 0) > 3) soft.push(e.device_count + ' devices');
  }
  return { ok: hard.length === 0, hard, soft };
}

function FlagChips({ verdict, loadingElig }) {
  if (loadingElig) return null;
  const v = verdict;
  return (
    <span>
      <Chip tone={v.ok ? 'green' : 'red'}>{v.ok ? 'eligible' : 'policy denial'}</Chip>
      {v.hard.map((r, i) => <Chip key={'h' + i} tone='red'>{r}</Chip>)}
      {v.soft.map((r, i) => <Chip key={'s' + i} tone='amber'>{r}</Chip>)}
    </span>
  );
}

// -----------------------------------------------------------------------------
// RefundQueue (May 2026) — admin-only screen showing two parallel lists:
// 1. Eligible ANNUAL purchases — ANNUAL pass rows < 14 days old, not yet
//    refunded, with a "Record Play refund" CTA on each.
// 2. Pending refund_requests rows — user-initiated requests awaiting review,
//    with Approve / Deny buttons. Passage is billed by Google Play: the money
//    is refunded in Play Console, then Approve records it through
//    admin_record_play_refund; deny marks the request as denied with a reason.
// -----------------------------------------------------------------------------
export default function RefundQueue() {
  const [eligibleAnnual, setEligibleAnnual] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [actionMsgType, setActionMsgType] = useState('ok');
  const [working, setWorking] = useState(null);  // pass_id or request_id currently being processed
  const [denyReasonFor, setDenyReasonFor] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [myRole, setMyRole] = useState('admin');
  const [eligMap, setEligMap] = useState(null);  // null = loading, {} = loaded

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) setMyRole(getUserRole(data.user.email || ''));
    });
  }, []);
  const canFinancial = myRole === 'c_suite' || myRole === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cutoff = new Date(Date.now() - 14 * 86400000).toISOString();
      const [annualResp, requestsResp] = await Promise.all([
        supabase.from('user_passes')
          .select('id,user_id,pass_type,created_at,expires_at,stripe_session_id,stripe_payment_intent_id,refunded_at,cancelled_at,cancel_reason')
          .in('pass_type', ['ANNUAL', 'annual'])
          .gte('created_at', cutoff)
          .is('refunded_at', null)
          .order('created_at', { ascending: false })
          .limit(200),
        supabase.from('refund_requests')
          .select('id,user_id,email,pass_type,price_usd,stripe_payment_id,eligible,reason,notes,status,created_at,pass_id,processed_at,denial_reason')
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(200),
      ]);
      setEligibleAnnual(annualResp.data || []);
      setRefundRequests(requestsResp.data || []);
      try {
        const { data: elig } = await supabase.rpc('admin_refund_eligibility');
        const map = {};
        (Array.isArray(elig) ? elig : []).forEach(e => { if (e && e.user_id) map[e.user_id] = e; });
        setEligMap(map);
      } catch (_e2) { setEligMap({}); }
    } catch (_e) { /* swallow */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---- Record a Play refund on an ANNUAL pass ----
  const issueRefund = async (pass) => {
    window.open(PLAY_CONSOLE_URL, '_blank', 'noopener');
    if (!window.confirm('Refund the ANNUAL order in Google Play Console first. Has the $' + PASSAGE_PRICES.ANNUAL + ' refund for pass ' + pass.id.slice(0, 8) + '… been issued there? Recording it counts against the user\'s lifetime refund quota.')) return;
    setWorking(pass.id);
    setActionMsg(''); setActionMsgType('ok');
    try {
      const { error } = await supabase.rpc('admin_record_play_refund', { p_pass_id: pass.id, p_reason: 'admin_initiated_14d_window' });
      if (error) throw new Error(error.message);
      setActionMsg('Refund recorded for pass ' + pass.id.slice(0, 8) + '… (issued in Google Play Console).');
      setActionMsgType('ok');
      load();
    } catch (_e) {
      setActionMsg('Refund failed: ' + _e.message); setActionMsgType('warn');
    }
    setWorking(null);
  };

  // ---- Approve a refund_request row ----
  const approveRequest = async (req) => {
    if (!req.pass_id) { setActionMsg('This request has no pass attached — find the order in Google Play Console.'); setActionMsgType('warn'); return; }
    window.open(PLAY_CONSOLE_URL, '_blank', 'noopener');
    if (!window.confirm('Refund the order in Google Play Console first. Has the refund been issued there?')) return;
    setWorking(req.id);
    setActionMsg(''); setActionMsgType('ok');
    try {
      // admin_record_play_refund also marks the request approved.
      const { error } = await supabase.rpc('admin_record_play_refund', { p_pass_id: req.pass_id, p_request_id: req.id, p_reason: 'admin_approved_request' });
      if (error) throw new Error(error.message);
      setActionMsg('Refund approved and recorded (issued in Google Play Console).');
      setActionMsgType('ok');
      load();
    } catch (_e) {
      setActionMsg('Approval failed: ' + _e.message); setActionMsgType('warn');
    }
    setWorking(null);
  };

  // ---- Deny a refund_request row ----
  const denyRequest = async (req) => {
    if (!denyReason.trim()) { setActionMsg('Denial reason required.'); setActionMsgType('warn'); return; }
    setWorking(req.id);
    setActionMsg(''); setActionMsgType('ok');
    try {
      const { error } = await supabase.from('refund_requests').update({
        status: 'denied',
        denial_reason: denyReason.trim(),
        processed_at: new Date().toISOString(),
      }).eq('id', req.id);
      if (error) throw error;
      setActionMsg('Request denied.'); setActionMsgType('ok');
      setDenyReasonFor(null); setDenyReason('');
      load();
    } catch (_e) {
      setActionMsg('Denial failed: ' + _e.message); setActionMsgType('warn');
    }
    setWorking(null);
  };

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>
          Refund queue
        </h2>
        <p style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          Annual purchases within the 14-day refund window and pending user-submitted refund requests. Refunds are only allowed on ANNUAL; MONTHLY is final. Passage is billed by Google Play: refund the order in Play Console, then record it here.
        </p>
      </div>

      <Grid cols={3}>
        <MetricCard label="Eligible ANNUAL refunds" value={loading ? '…' : fmt(eligibleAnnual.length)}
          sub={loading ? undefined : 'within 14d window'} accent="#9F7E4F" />
        <MetricCard label="Pending requests" value={loading ? '…' : fmt(refundRequests.length)}
          sub={loading ? undefined : 'awaiting review'} accent="#dc3545" />
        <MetricCard label="Your role" value={canFinancial ? 'Admin / C-suite' : 'Read-only'}
          sub={canFinancial ? 'can issue refunds' : 'view only'}
          accent={canFinancial ? '#28a745' : '#9eb8c8'} />
      </Grid>

      {actionMsg && <Insight type={actionMsgType === 'ok' ? 'ok' : 'warn'} style={{ marginBottom: 12 }}>{actionMsg}</Insight>}

      {/* ── Eligible ANNUAL list ─────────────────────────────────────────────── */}
      <Card>
        <CardTitle>ANNUAL purchases &lt; 14 days old ({fmt(eligibleAnnual.length)})</CardTitle>
        {loading ? (
          <><Skel /><Skel /><Skel /></>
        ) : eligibleAnnual.length === 0 ? (
          <Insight type="info">No ANNUAL purchases in the 14-day refund window.</Insight>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.72rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>User</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Pass ID</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Purchased</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Days old</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Window remaining</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Flags</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {eligibleAnnual.map(p => {
                  const d = daysSince(p.created_at) || 0;
                  const remaining = Math.max(0, 14 - d);
                  const ev = eligVerdict(eligMap && eligMap[p.user_id], 'ANNUAL', p.created_at);
                  const refundable = canFinancial && ev.ok;
                  return (
                    <tr key={p.id} style={{ borderBottom: '.5px solid var(--color-border-tertiary)' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '.65rem', color: 'var(--color-text-primary)' }}>
                        {p.user_id ? p.user_id.slice(0, 8) + '…' : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '.6rem', color: 'var(--color-text-secondary)' }}>
                        {p.id.slice(0, 8)}…
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--color-text-secondary)' }}>{fmtDate(p.created_at)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--color-text-primary)' }}>{d}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500,
                        color: remaining > 7 ? '#28a745' : remaining > 2 ? '#fd7e14' : '#dc3545' }}>
                        {remaining}d
                      </td>
                      <td style={{ padding: '6px 8px', maxWidth: 220 }}>
                        <FlagChips verdict={ev} loadingElig={eligMap == null} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        <button onClick={() => refundable && issueRefund(p)}
                          disabled={!refundable || working === p.id}
                          title={ev.ok ? 'Refund in Google Play Console, then record it here' : 'Blocked by policy: ' + ev.hard.join('; ')}
                          style={{ fontSize: '.62rem', padding: '4px 10px', borderRadius: 6,
                            background: refundable ? '#9F7E4F' : '#9aaab3', color: '#fff',
                            border: 'none', cursor: refundable ? 'pointer' : 'not-allowed', fontFamily: F }}>
                          {working === p.id ? '…' : ev.ok ? 'Record Play refund' : 'Blocked'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Pending refund_requests list ─────────────────────────────────────── */}
      <Card>
        <CardTitle>Pending refund requests ({fmt(refundRequests.length)})</CardTitle>
        {loading ? (
          <><Skel /><Skel /></>
        ) : refundRequests.length === 0 ? (
          <Insight type="info">No pending refund requests.</Insight>
        ) : refundRequests.map(req => {
          const isDenying = denyReasonFor === req.id;
          const isWorking = working === req.id;
          const passAge = req.created_at ? daysSince(req.created_at) : null;
          const ev = eligVerdict(eligMap && eligMap[req.user_id], req.pass_type, null);
          const approvable = canFinancial && ev.ok;
          return (
            <div key={req.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--color-border-tertiary)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: '.78rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {req.email || (req.user_id ? req.user_id.slice(0, 8) + '…' : 'Unknown user')}
                    {req.pass_type && (
                      <span style={{ marginLeft: 8, fontSize: '.6rem', padding: '1px 7px', borderRadius: 8,
                        background: '#5a8fa618', color: '#5a8fa6', fontWeight: 500 }}>
                        {req.pass_type} · ${req.price_usd || priceOf(req.pass_type) || '?'}
                      </span>
                    )}
                    {req.eligible === false && (
                      <span style={{ marginLeft: 6, fontSize: '.6rem', padding: '1px 7px', borderRadius: 8,
                        background: '#dc354518', color: '#dc3545', fontWeight: 500 }}>
                        flagged ineligible
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                    Submitted {fmtDate(req.created_at)} ({passAge}d ago)
                  </div>
                  <div style={{ marginTop: 2 }}>
                    <FlagChips verdict={ev} loadingElig={eligMap == null} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => approvable && approveRequest(req)}
                    disabled={!approvable || isWorking}
                    title={ev.ok ? 'Refund in Google Play Console, then record it here' : 'Blocked by policy: ' + ev.hard.join('; ')}
                    style={{ fontSize: '.62rem', padding: '4px 10px', borderRadius: 6,
                      background: approvable ? '#28a745' : '#9aaab3', color: '#fff',
                      border: 'none', cursor: approvable ? 'pointer' : 'not-allowed', fontFamily: F }}>
                    {isWorking ? '…' : ev.ok ? 'Approve & record Play refund' : 'Blocked'}
                  </button>
                  {!ev.ok && (
                    <button onClick={() => { setDenyReasonFor(req.id); setDenyReason('Per policy: ' + ev.hard.join('; ') + '.'); }}
                      disabled={!canFinancial || isWorking}
                      style={{ fontSize: '.62rem', padding: '4px 10px', borderRadius: 6,
                        background: '#dc3545', color: '#fff', border: 'none',
                        cursor: canFinancial ? 'pointer' : 'not-allowed', fontFamily: F }}>
                      Deny per policy
                    </button>
                  )}
                  <button onClick={() => { setDenyReasonFor(req.id); setDenyReason(''); }}
                    disabled={!canFinancial || isWorking}
                    style={{ fontSize: '.62rem', padding: '4px 10px', borderRadius: 6,
                      background: canFinancial ? 'transparent' : '#9aaab3', color: '#dc3545',
                      border: '1px solid ' + (canFinancial ? '#dc354540' : '#9aaab3'),
                      cursor: canFinancial ? 'pointer' : 'not-allowed', fontFamily: F }}>
                    Deny
                  </button>
                </div>
              </div>
              {req.reason && (
                <div style={{ fontSize: '.7rem', color: 'var(--color-text-primary)', padding: '6px 10px',
                  background: 'var(--color-background-secondary)', borderRadius: 6, marginBottom: 4 }}>
                  <span style={{ fontWeight: 500 }}>Reason:</span> {req.reason}
                </div>
              )}
              {req.notes && (
                <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                  Notes: {req.notes}
                </div>
              )}
              {isDenying && (
                <div style={{ marginTop: 6, padding: '8px 10px', background: '#fff5f0', borderRadius: 6 }}>
                  <div style={{ fontSize: '.6rem', color: '#dc3545', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    Denial reason (required)
                  </div>
                  <textarea value={denyReason} onChange={e => setDenyReason(e.target.value)}
                    placeholder="e.g. Outside 14-day window / account flagged for abuse / lifetime refund quota exceeded"
                    style={{ width: '100%', minHeight: 36, padding: '6px 8px', borderRadius: 5,
                      border: '1px solid #dc354540', background: '#fff', fontSize: '.7rem',
                      fontFamily: F, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => { setDenyReasonFor(null); setDenyReason(''); }}
                      style={{ fontSize: '.6rem', padding: '3px 10px', borderRadius: 5,
                        background: 'transparent', color: 'var(--color-text-secondary)',
                        border: '1px solid var(--color-border-secondary)', cursor: 'pointer', fontFamily: F }}>
                      Cancel
                    </button>
                    <button onClick={() => denyRequest(req)}
                      disabled={!denyReason.trim() || isWorking}
                      style={{ fontSize: '.6rem', padding: '3px 10px', borderRadius: 5,
                        background: '#dc3545', color: '#fff', border: 'none',
                        cursor: denyReason.trim() ? 'pointer' : 'not-allowed', fontFamily: F }}>
                      Confirm denial
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </Card>

      <Insight type="info">
        <strong>Policy reminder.</strong> Only ANNUAL has a 14-day refund window. MONTHLY is all sales final on the current period — deny those requests with that reason. Refunds for flagged accounts, repeat-refund accounts (lifetime_refund_count ≥ 1), and accounts with active locks must be denied. The money is refunded in Google Play Console; admin_record_play_refund then enforces every check server-side (ANNUAL-only, 14-day window, flags, suspension, lifetime quota) and returns the precise policy reason on refusal — the chips above mirror exactly what it will decide.
      </Insight>
    </div>
  );
}
